import { observable, action, runInAction, makeObservable } from 'mobx';
import { injectable, inject } from '@servicetitan/react-ioc';
import { FormState, FieldState } from 'formstate';
import { DropdownFieldState } from '@servicetitan/form';
import { Select } from '@servicetitan/design-system';
import {
    CampaignCostApi,
    MarketingChannel,
    CampaignMonthCostModel,
} from '@servicetitan/application-data/marketing';

type DropdownItemProps = Select['options'][0];

const defaultYear = new Date().getFullYear();
const startingYear = new Date().getFullYear() - 1;
const yearsCount = 3;

type CostFieldType = {
    day: FieldState<number>;
    month: FieldState<number>;
};

export class CostField extends FormState<CostFieldType> {
    @observable daysCount!: number;

    constructor(daysCount: number, day?: number, month?: number) {
        super({
            day: new FieldState(
                day !== undefined ? day : month !== undefined ? month / daysCount : 0
            ),
            month: new FieldState(
                day !== undefined ? day * daysCount : month !== undefined ? month : 0
            ),
        });

        makeObservable(this);

        this.daysCount = daysCount;
    }

    onDayChange = (day: number) => {
        this.$.day.onChange(day);
        this.$.month.onChange(day * this.daysCount);
    };

    onMonthChange = (month: number) => {
        this.$.month.onChange(month);
        this.$.day.onChange(month / this.daysCount);
    };
}

@injectable()
export class CampaignCostDrawerStore {
    @inject(CampaignCostApi) costApi!: CampaignCostApi;

    @observable costs = new FormState([] as CostField[]);
    @observable loading = false;
    @observable saving = false;

    channel?: MarketingChannel;
    campaignId?: number;
    yearOptions: DropdownItemProps[] = [...Array(yearsCount).keys()].map(i => ({
        text: startingYear + i,
        value: i,
    }));
    year = new DropdownFieldState(
        this.yearOptions.find(x => x.text === defaultYear)!.value as number
    );

    constructor() {
        makeObservable(this);
    }

    @action
    async getVariableMonthlyCosts(channel: MarketingChannel, campaignId: number) {
        this.loading = true;
        const result = await this.costApi.getVariableMonthlyCosts(
            channel,
            campaignId,
            startingYear,
            yearsCount
        );

        runInAction(() => {
            this.costs = this.getCosts(result.data.costs);
            this.loading = false;
        });
    }

    @action
    async save() {
        if (this.campaignId === undefined || this.channel === undefined) {
            return;
        }

        if (this.saving) {
            return;
        }

        this.saving = true;

        const costsJs = this.costs.$.map(cost => cost.$.day.value);

        await this.costApi.saveVariableMonthlyCosts(
            this.channel,
            this.campaignId,
            startingYear,
            yearsCount,
            costsJs
        );
        runInAction(() => {
            this.saving = false;
        });
    }

    init(campaignId: number, channel: MarketingChannel) {
        this.campaignId = campaignId;
        this.channel = channel;
        this.getVariableMonthlyCosts(channel, campaignId);
    }

    getCosts(costs: CampaignMonthCostModel[] = []) {
        costs.sort((cost1, cost2) => {
            return cost1.year * 12 + cost1.month - (cost2.year * 12 + cost2.month);
        });

        const costFields = [] as CostField[];
        costs.forEach(cost => {
            costFields.push(new CostField(cost.daysInMonth, cost.dailyCost));
        });

        return new FormState(costFields);
    }
}
